import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  ClipboardCheck,
  User,
  Car,
  Truck,
  Package,
  ShoppingCart,
  ShoppingBag,
  RotateCcw,
  Send,
  Receipt,
  CreditCard,
  ShieldCheck,
  BarChart3,
  Users,
  Wrench,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Bell,
  Search as SearchIcon,
  Sparkles,
  LifeBuoy,
  BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSync } from "@/context/SyncContext";
import { useI18n } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LangSwitcher from "@/components/LangSwitcher";
import { LanguagePickerSplash } from "@/components/LanguagePickerSplash";
import GlobalSearch from "@/components/GlobalSearch";
import QuickCreateMenu from "@/components/QuickCreateMenu";
import NotificationsDrawer, { useUnreadCount } from "@/components/NotificationsDrawer";
import QuickToolsDrawer from "@/components/QuickToolsDrawer";

// ---------- Navigation config ----------
// Flat list (no category headers). "Spare Parts" is collapsible with children.
const NAV = [
  { to: "/dashboard",    label: "Dashboard",     Icon: LayoutDashboard, roles: ["owner", "branch_manager", "advisor", "storekeeper", "driver", "mechanic", "cashier", "accountant"] },
  { to: "/estimates",    label: "Estimates",     Icon: FileText,        roles: ["owner", "branch_manager", "advisor", "cashier", "accountant"] },
  { to: "/appointments", label: "Appointments",  Icon: Calendar,        roles: ["owner", "branch_manager", "advisor"] },
  { to: "/job-cards",    label: "Job Cards",     Icon: FileText,        roles: ["owner", "branch_manager", "advisor", "mechanic", "storekeeper", "accountant"] },
  { to: "/my-jobs",      label: "My Jobs",       Icon: Wrench,          roles: ["mechanic", "owner", "advisor", "branch_manager"] },
  {
    key: "spare-parts", label: "Spare Parts", Icon: Package,
    roles: ["owner", "branch_manager", "storekeeper", "advisor", "cashier"],
    to: "/spare-parts", match: ["/spare-parts", "/inventory"], end: true,
    children: [
      { to: "/spare-parts/stock",          label: "Stock",          Icon: Package,      roles: ["owner", "branch_manager", "storekeeper", "advisor"] },
      { to: "/spare-parts/purchases",      label: "Purchases",      Icon: ShoppingCart, roles: ["owner", "branch_manager", "storekeeper"] },
      { to: "/spare-parts/counter-sales",  label: "Counter Sales",  Icon: ShoppingBag,  roles: ["owner", "branch_manager", "storekeeper", "cashier"] },
      { to: "/spare-parts/returns",        label: "Returns",        Icon: RotateCcw,    roles: ["owner", "branch_manager", "storekeeper"] },
      { to: "/spare-parts/vendors",        label: "Vendors",        Icon: Truck,        roles: ["owner", "branch_manager", "storekeeper"] },
      { to: "/spare-parts/reorder",        label: "Reorder Alerts", Icon: Bell,         roles: ["owner", "branch_manager", "storekeeper"] },
    ],
  },
  { to: "/invoices",      label: "Invoices",       Icon: Receipt,      roles: ["owner", "branch_manager", "advisor", "cashier", "accountant"] },
  { to: "/gate-pass",     label: "Gate Pass",      Icon: ShieldCheck,  roles: ["owner", "branch_manager", "advisor", "cashier", "accountant", "security"] },
  { to: "/parts-catalogue", label: "Parts Catalogue", Icon: BookOpen,   roles: ["owner", "branch_manager", "advisor", "mechanic", "storekeeper"] },
  { to: "/enquiries",     label: "Part Enquiries", Icon: Send,         roles: ["owner", "branch_manager", "advisor", "storekeeper"] },
  { to: "/customers",     label: "Customers",      Icon: User,         roles: ["owner", "branch_manager", "advisor", "cashier"] },
  { to: "/vehicles",      label: "Vehicles",       Icon: Car,          roles: ["owner", "branch_manager", "advisor"] },
  { to: "/reports",       label: "Reports",        Icon: BarChart3,    roles: ["owner", "branch_manager", "advisor", "accountant"] },
  { to: "/support",       label: "Support",        Icon: LifeBuoy,     roles: ["owner", "branch_manager", "advisor", "mechanic", "storekeeper", "driver", "cashier", "accountant"] },
  { to: "/settings",      label: "Settings",       Icon: Settings,     roles: ["owner", "branch_manager"] },
];

// ---------- Sync badge (compact) ----------
const SyncPill = () => {
  const { state, online, pending, flush } = useSync();
  const off = !online || state === "offline";
  if (off) {
    return (
      <button
        onClick={flush}
        data-testid="sync-status-offline"
        className="inline-flex items-center gap-1.5 px-2 h-8 rounded-md bg-[#e63946]/10 text-[#e63946] border border-[#e63946]/25 text-[11px] font-semibold"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#e63946] status-pulse" />
        OFFLINE
        {pending > 0 && <span className="ml-1 px-1 bg-[#e63946] text-white rounded font-bold text-[10px]">{pending}</span>}
      </button>
    );
  }
  if (state === "syncing") {
    return (
      <button
        onClick={flush}
        data-testid="sync-status-syncing"
        className="inline-flex items-center gap-1.5 px-2 h-8 rounded-md bg-[#F79009]/10 text-[#F79009] border border-[#F79009]/25 text-[11px] font-semibold"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#F79009] status-pulse" />
        SYNCING
        {pending > 0 && <span className="ml-1 px-1 bg-[#F79009] text-white rounded font-bold text-[10px]">{pending}</span>}
      </button>
    );
  }
  return (
    <div
      data-testid="sync-status-online"
      className="inline-flex items-center gap-1.5 px-2 h-8 rounded-md bg-[#12B76A]/10 text-[#12B76A] border border-[#12B76A]/25 text-[11px] font-semibold"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-[#12B76A]" />
      SYNCED
    </div>
  );
};

// ---------- Breadcrumbs from URL ----------
const CRUMB_LABELS = {
  dashboard: "Dashboard",
  "job-cards": "Job Cards",
  "my-jobs": "My Jobs",
  "my-day": "My Day",
  customers: "Customers",
  vehicles: "Vehicles",
  inventory: "Inventory",
  "spare-parts": "Spare Parts",
  stock: "Stock",
  vendors: "Vendors",
  reorder: "Reorder Alerts",
  purchases: "Purchases",
  "counter-sales": "Counter Sales",
  returns: "Returns",
  enquiries: "Parts Enquiries",
  "gate-pass": "Gate Pass",
  "pickup-drop": "Pickup & Drop",
  invoices: "Invoices",
  estimates: "Estimates",
  payments: "Payments",
  reports: "Reports",
  users: "Team",
  new: "New",
};

const Breadcrumbs = () => {
  const loc = useLocation();
  const parts = loc.pathname.split("/").filter(Boolean);
  return (
    <div className="hidden md:flex items-center gap-1 text-[12px] text-[#667085] min-w-0">
      {parts.map((p, i) => (
        <span key={`${p}-${i}`} className="inline-flex items-center gap-1 min-w-0">
          {i > 0 && <span className="text-[#a8dadc]">/</span>}
          <span className={`truncate ${i === parts.length - 1 ? "text-[#1d3557] font-semibold" : ""}`}>
            {CRUMB_LABELS[p] || p.replace(/-/g, " ")}
          </span>
        </span>
      ))}
    </div>
  );
};

// Collapsible parent (e.g. Spare Parts with children)
const SidebarGroup = ({ item, items, collapsed }) => {
  const location = useLocation();
  const insideGroup =
    location.pathname === item.to ||
    location.pathname.startsWith(item.to + "/");
  const [open, setOpen] = useState(insideGroup);
  useEffect(() => {
    if (insideGroup) setOpen(true);
  }, [insideGroup]);

  // Collapsed sidebar → render only the parent as a link (no chevron)
  if (collapsed) {
    return <SidebarItem item={item} collapsed={collapsed} />;
  }

  const testKey = item.to.slice(1).replace(/\//g, "-");
  return (
    <div data-testid={`nav-group-${testKey}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-group-toggle-${testKey}`}
        className={`group relative flex items-center gap-3 px-3 h-10 mx-2 rounded-md text-[13px] w-[calc(100%-16px)] transition-colors ${
          insideGroup
            ? "bg-white/10 text-white font-semibold"
            : "text-[#a8dadc] hover:bg-white/5 hover:text-white"
        }`}
      >
        <item.Icon size={16} strokeWidth={2} />
        <span className="flex-1 truncate text-left">{item.label}</span>
        <ChevronDown
          size={13}
          className={`transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open && (
        <div className="mt-0.5">
          {/* Optional: direct link to parent index */}
          <NavLink
            to={item.to}
            end
            data-testid={`nav-${testKey}-all`}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 pl-9 pr-3 h-9 mx-2 rounded-md text-[12.5px] transition-colors ${
                isActive
                  ? "bg-[#e63946] text-white font-semibold"
                  : "text-[#a8dadc] hover:bg-white/5 hover:text-white"
              }`
            }
          >
            All Parts
          </NavLink>
          {items.map((c) => (
            <SidebarItem key={c.to} item={{ ...c, indent: true }} collapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Sidebar ----------
const SidebarItem = ({ item, collapsed }) => (
  <NavLink
    to={item.to}
    end={!!item.end}
    data-testid={`nav-${item.to.slice(1).replace(/\//g, "-")}`}
    onClick={(e) => {
      if (item.soon) {
        e.preventDefault();
      }
    }}
    className={({ isActive }) =>
      `group relative flex items-center ${collapsed ? "justify-center px-2" : `gap-3 ${item.indent ? "pl-8 pr-3" : "px-3"}`} h-10 mx-2 rounded-md text-[13px] transition-colors ${
        isActive && !item.soon
          ? "bg-[#e63946] text-white font-semibold"
          : item.soon
          ? "text-[#a8dadc]/60 cursor-not-allowed"
          : "text-[#a8dadc] hover:bg-white/5 hover:text-white"
      }`
    }
    title={collapsed ? item.label + (item.soon ? " — coming soon" : "") : undefined}
  >
    <item.Icon size={item.indent ? 14 : 16} strokeWidth={2} />
    {!collapsed && (
      <span className="flex-1 truncate">{item.label}</span>
    )}
    {!collapsed && item.soon && (
      <span className="text-[9px] font-bold uppercase tracking-wider bg-white/10 text-[#a8dadc] px-1.5 py-0.5 rounded">
        Soon
      </span>
    )}
  </NavLink>
);

const Sidebar = ({ collapsed, onToggle }) => {
  const { user, garage } = useAuth();
  const role = user?.role || "";
  return (
    <aside
      className={`shrink-0 bg-[#1d3557] text-white flex flex-col h-screen sticky top-0 transition-[width] duration-200 ${
        collapsed ? "w-[76px]" : "w-[248px]"
      }`}
      data-testid="app-sidebar"
    >
      {/* Logo + garage */}
      <div className="px-3 py-4 border-b border-white/10 flex items-center gap-2">
        <img src="/fender-icon.png" alt="Fender" className="w-9 h-9 shrink-0" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-bold text-[15px] leading-tight text-white">Fender</div>
            <div className="text-[10px] uppercase tracking-widest text-[#a8dadc]">by SparesHub</div>
          </div>
        )}
        <button
          onClick={onToggle}
          data-testid="sidebar-toggle"
          className="p-1 rounded hover:bg-white/10 text-[#a8dadc] hover:text-white transition-colors"
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
      {!collapsed && garage?.name && (
        <div className="px-4 pt-3 pb-2 text-[11px] text-[#a8dadc] uppercase tracking-widest font-semibold truncate" data-testid="sidebar-garage-name">
          {garage.name}
        </div>
      )}

      {/* Flat nav — no category headers */}
      <nav className="flex-1 overflow-y-auto no-scrollbar py-2">
        {NAV.map((it) => {
          if (!it.roles.includes(role)) return null;
          if (it.children) {
            const visibleChildren = it.children.filter((c) => c.roles.includes(role));
            return (
              <SidebarGroup
                key={it.key || it.to}
                item={it}
                items={visibleChildren}
                collapsed={collapsed}
              />
            );
          }
          return <SidebarItem key={it.to} item={it} collapsed={collapsed} />;
        })}
      </nav>

      {/* Footer / user */}
      <div className="border-t border-white/10 p-3">
        {!collapsed ? (
          <UserBox />
        ) : (
          <div className="grid place-items-center">
            <UserAvatar small />
          </div>
        )}
      </div>
    </aside>
  );
};

const UserAvatar = ({ small }) => {
  const { user } = useAuth();
  const initial = (user?.name || user?.email || "?")[0]?.toUpperCase();
  return (
    <div
      className={`${small ? "w-8 h-8 text-[12px]" : "w-9 h-9 text-[13px]"} rounded-full bg-[#457b9d] text-white font-bold grid place-items-center`}
    >
      {initial}
    </div>
  );
};

const UserBox = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="sidebar-user-btn"
          className="w-full flex items-center gap-2 rounded-md hover:bg-white/5 p-2 text-left"
        >
          <UserAvatar />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-white truncate" data-testid="sidebar-user-name">
              {user?.name || user?.email}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#a8dadc] font-bold">{user?.role}</div>
          </div>
          <ChevronDown size={13} className="text-[#a8dadc]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56 rounded-lg bg-white border border-[#E4E7EC] shadow-lg p-1">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Account</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#E4E7EC]" />
        <DropdownMenuItem
          onClick={() => nav("/settings")}
          className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2 flex items-center gap-2"
        >
          <Settings size={14} className="text-[#457b9d]" /> Settings
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => window.open("mailto:contact@fenderapp.com", "_blank")}
          className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2 flex items-center gap-2"
        >
          <HelpCircle size={14} className="text-[#457b9d]" /> Help & support
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#E4E7EC]" />
        <DropdownMenuItem
          onClick={() => { logout(); nav("/"); }}
          data-testid="sidebar-logout-btn"
          className="cursor-pointer rounded-md text-[13px] text-[#e63946] hover:bg-[#e63946]/10 focus:bg-[#e63946]/10 px-2 py-2 flex items-center gap-2"
        >
          <LogOut size={14} /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ---------- Top Header ----------
const TopHeader = ({ onOpenSearch, onOpenNotif, onOpenQT, unread }) => {
  const { t } = useI18n();
  return (
    <header
      className="h-14 bg-white border-b border-[#E4E7EC] flex items-center gap-3 px-4 md:px-6 sticky top-0 z-30"
      data-testid="app-topheader"
    >
      <Breadcrumbs />
      <div className="flex-1 flex justify-center max-w-xl mx-auto">
        <button
          onClick={onOpenSearch}
          data-testid="global-search-trigger"
          className="hidden sm:flex items-center gap-2 w-full max-w-md h-9 px-3 rounded-md bg-[#F7F9FC] border border-[#E4E7EC] text-[13px] text-[#667085] hover:border-[#457b9d] hover:bg-white transition-colors"
        >
          <SearchIcon size={14} />
          <span className="flex-1 text-left">{t("search_placeholder") || "Search job cards, customers, vehicles…"}</span>
          <kbd className="hidden md:inline-block text-[10px] font-mono text-[#667085] bg-white border border-[#E4E7EC] rounded px-1.5">
            Ctrl K
          </kbd>
        </button>
        <button
          onClick={onOpenSearch}
          data-testid="global-search-trigger-mobile"
          className="sm:hidden w-9 h-9 grid place-items-center rounded-md text-[#457b9d] hover:bg-[#F1FAEE]"
        >
          <SearchIcon size={16} />
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <SyncPill />
        <div className="hidden md:block">
          <LangSwitcher />
        </div>
        <button
          onClick={onOpenQT}
          data-testid="topbar-quicktools-btn"
          className="w-9 h-9 grid place-items-center rounded-md text-[#457b9d] hover:bg-[#F1FAEE] hover:text-[#1d3557]"
          title="Quick Tools"
        >
          <Sparkles size={16} />
        </button>
        <button
          onClick={onOpenNotif}
          data-testid="topbar-notifications-btn"
          className="relative w-9 h-9 grid place-items-center rounded-md text-[#457b9d] hover:bg-[#F1FAEE] hover:text-[#1d3557]"
          title="Notifications"
        >
          <Bell size={16} />
          {unread > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 grid place-items-center rounded-full bg-[#e63946] text-white text-[10px] font-bold">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
        <QuickCreateMenu />
      </div>
    </header>
  );
};

// ---------- Lifecycle banner ----------
// Renders a slim top-of-content banner when the workshop needs the owner's
// attention — email unverified, or workshop is in read-only mode after 30 days
// of inactivity. Both states come from AuthContext.garage.
const LifecycleBanner = () => {
  const { user, garage } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (!user || !garage) return null;

  const unverified = garage.email_verified === false;
  const readOnly = garage.read_only === true;
  if (!unverified && !readOnly) return null;

  const resend = async () => {
    if (!garage.email) return;
    setSending(true);
    try {
      const { api } = await import("@/lib/api");
      await api.post("/auth/resend-verification", { email: garage.email });
      setSent(true);
    } catch { /* ignore — 200 always */ }
    finally { setSending(false); }
  };

  if (readOnly) {
    return (
      <div className="px-4 md:px-6 py-2 bg-[#F79009]/10 border-b border-[#F79009]/30 text-[#B54708] text-[12.5px] flex items-center gap-3" data-testid="lifecycle-banner-readonly">
        <span>⚠ Your workshop is in <b>read-only mode</b> after 30 days of inactivity. Check the owner&apos;s inbox for a reactivation link. Editing is paused until then.</span>
      </div>
    );
  }
  return (
    <div className="px-4 md:px-6 py-2 bg-[#a8dadc]/25 border-b border-[#457b9d]/30 text-[#1d3557] text-[12.5px] flex items-center gap-3 flex-wrap" data-testid="lifecycle-banner-unverified">
      <span>ℹ Your workshop email <b>{garage.email}</b> is not verified yet. Verify within 7 days of signup to avoid a lockout.</span>
      {user.role === "owner" && (
        sent ? (
          <span className="text-[#16a34a] font-semibold">✓ Verification email sent</span>
        ) : (
          <button
            onClick={resend}
            disabled={sending}
            data-testid="lifecycle-banner-resend"
            className="h-7 px-3 rounded-md bg-[#1d3557] text-white text-[12px] font-semibold hover:bg-[#152841] disabled:opacity-50"
          >
            {sending ? "Sending…" : "Resend email"}
          </button>
        )
      )}
    </div>
  );
};

// ---------- AppShell ----------
export default function AppShell() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("fender_sidebar_collapsed") === "1");
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [qtOpen, setQtOpen] = useState(false);
  const { unread, refresh } = useUnreadCount();

  useEffect(() => {
    localStorage.setItem("fender_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  // Ctrl+K / Cmd+K opens search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex bg-[#F7F9FC]" style={{ fontFamily: '"Noto Sans","Noto Sans Devanagari","Noto Sans Kannada","Noto Sans Tamil","Noto Sans Malayalam",Arial,sans-serif' }}>
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopHeader
          onOpenSearch={() => setSearchOpen(true)}
          onOpenNotif={() => setNotifOpen(true)}
          onOpenQT={() => setQtOpen(true)}
          unread={unread}
        />
        <LifecycleBanner />
        <main className="flex-1 p-4 md:p-8 min-w-0">
          <Outlet />
        </main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      <NotificationsDrawer open={notifOpen} onOpenChange={setNotifOpen} onChange={refresh} />
      <QuickToolsDrawer open={qtOpen} onOpenChange={setQtOpen} />
      <LanguagePickerSplash />
    </div>
  );
}
